import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseLiveSpeechReturn {
  isSupported: boolean;
  isListening: boolean;
  error: string | null;
  /** Combined final + interim text produced since start() */
  liveText: string;
  /** Only the committed (final) portion */
  finalText: string;
  start: (baseText: string) => void;
  stop: () => string; // returns committed text to keep
}

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null;

export function useLiveSpeechDictation(lang = 'en-US'): UseLiveSpeechReturn {
  const isSupported = !!SpeechRecognitionAPI;

  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');

  const recognitionRef = useRef<any>(null);
  const isStoppingRef = useRef(false);
  const hadErrorRef = useRef(false);
  const finalAccRef = useRef(''); // accumulator to avoid stale closures

  const stop = useCallback((): string => {
    isStoppingRef.current = true;
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimText('');
    const committed = finalAccRef.current;
    return committed;
  }, []);

  const start = useCallback((_baseText: string) => {
    if (!isSupported) {
      setError("Live dictation not supported in this browser.");
      return;
    }

    // Clean previous
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
    }

    setError(null);
    setFinalText('');
    setInterimText('');
    finalAccRef.current = '';
    isStoppingRef.current = false;
    hadErrorRef.current = false;

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let finalSoFar = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalSoFar += transcript;
        } else {
          interim += transcript;
        }
      }
      finalAccRef.current = finalSoFar;
      setFinalText(finalSoFar);
      setInterimText(interim);
    };

    recognition.onerror = (event: any) => {
      const errorName = event.error as string;
      if (errorName === 'aborted' && isStoppingRef.current) return;
      if (errorName === 'no-speech') return; // benign

      hadErrorRef.current = true;
      let msg: string;
      switch (errorName) {
        case 'not-allowed':
          msg = 'Microphone permission denied.';
          break;
        case 'audio-capture':
          msg = 'No microphone found.';
          break;
        case 'network':
          msg = 'Network error during speech recognition.';
          break;
        default:
          msg = `Speech recognition error: ${errorName}`;
      }
      setError(msg);
      setIsListening(false);
    };

    recognition.onend = () => {
      if (!isStoppingRef.current && !hadErrorRef.current) {
        // Auto-restart for continuous listening
        try { recognition.start(); } catch { setIsListening(false); }
        return;
      }
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      setError('Failed to start speech recognition.');
    }
  }, [isSupported, lang]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isStoppingRef.current = true;
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
    };
  }, []);

  const liveText = finalText + interimText;

  return {
    isSupported,
    isListening,
    error,
    liveText,
    finalText,
    start,
    stop,
  };
}
